import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivityTracker } from '@/hooks/useActivityTracker';
import { useProactiveTokenRefresh } from '@/hooks/useProactiveTokenRefresh';
import { useAuth, useAuthActions } from '@/stores/auth';
import InactivityWarningDialog from '@/components/auth/InactivityWarningDialog';
import { LAST_ACTIVITY_STORAGE_KEY } from '@/utils/constants';

interface InactivityContextValue {
  remainingTime: number;
  extendSession: () => void;
  resetActivity: () => void;
}

const InactivityContext = createContext<InactivityContextValue | null>(null);

interface InactivityProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps authenticated routes with inactivity tracking
 *
 * Features:
 * - Tracks user activity (mouse, keyboard, touch, scroll)
 * - Shows warning dialog before session expires
 * - Auto-logout after inactivity timeout
 * - Cross-tab synchronization
 * - Proactive token refresh to keep active users logged in
 */
export function InactivityProvider({ children }: InactivityProviderProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { logout } = useAuthActions();

  // Enable proactive token refresh for authenticated users
  // This will automatically refresh tokens when they're about to expire
  // and the user has been active, implementing "inactivity-based" expiration
  useProactiveTokenRefresh(isAuthenticated);

  const {
    remainingTime,
    isWarningVisible,
    isTimedOut,
    extendSession,
    resetActivity,
  } = useActivityTracker(isAuthenticated);

  // Handle auto-logout when timed out
  useEffect(() => {
    if (isTimedOut && isAuthenticated) {
      handleInactivityLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimedOut, isAuthenticated]);

  const handleInactivityLogout = async () => {
    try {
      // Clear the activity timestamp
      localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);

      // Perform logout
      await logout();

      // Navigate to login with message
      navigate('/login', {
        state: {
          message: 'You have been logged out due to inactivity.',
          type: 'info',
        },
        replace: true,
      });
    } catch (error) {
      console.error('Error during inactivity logout:', error);
      // Force navigate to login even if logout fails
      navigate('/login', { replace: true });
    }
  };

  const handleExtendSession = () => {
    extendSession();
  };

  const handleLogoutNow = async () => {
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    await logout();
    navigate('/login', { replace: true });
  };

  const value: InactivityContextValue = {
    remainingTime,
    extendSession,
    resetActivity,
  };

  return (
    <InactivityContext.Provider value={value}>
      {children}

      {/* Inactivity Warning Dialog */}
      <InactivityWarningDialog
        isOpen={isWarningVisible && isAuthenticated}
        remainingTime={remainingTime}
        onExtendSession={handleExtendSession}
        onLogout={handleLogoutNow}
      />
    </InactivityContext.Provider>
  );
}

/**
 * Hook to access inactivity context
 * Must be used within an InactivityProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useInactivity(): InactivityContextValue {
  const context = useContext(InactivityContext);

  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }

  return context;
}
